import { useMutation } from "@apollo/client/react";
import type { DiffState } from "@components/design-system/organisms/DSNoteInput";
import ACCEPT_LLM_CUSTOMIZATIONS from "@graphql/mutations/AcceptLlmCustomization";
import GENERATE_LLM_CUSTOMIZATION from "@graphql/mutations/GenerateLLMCustomization";
import { useWatchUserWsEvent } from "@hooks";
import { useAuth } from "@hooks/use-auth";
import {
  selectPendingProposalForSection,
  usePendingWritingCompanionProposals,
} from "@hooks/use-pending-writing-companion-proposals";
import {
  UserPermissions,
  usePremiumFeature,
} from "@hooks/use-premium-feature";
import { useStreamResult } from "@hooks/use-stream-result";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { alert } from "../components/common/Alert";
import { noteSections } from "../components/notes/edit/form/noteConstants";
import {
  generateParams,
  getOptions,
} from "../components/notes/edit/form/utils";

type EntityType = "note" | "template";

const ENTITY_CONFIG = {
  note: {
    uuidField: "note_uuid",
    sectionField: "note_section",
    wsUpdateEvent: "companion_llm_customization_updates",
    wsCompletionEvent: "companion_llm_customization_completion",
  },
  template: {
    uuidField: "template_uuid",
    sectionField: "template_section",
    wsUpdateEvent: "companion_llm_template_customization_updates",
    wsCompletionEvent:
      "companion_llm_template_customization_completion",
  },
};

interface UseWritingAssistantParams {
  entityType: EntityType;
  entityUuid: string;
  fieldName: string;
  getFieldValue: () => string;
  setFieldValue: (value: string) => void;
  /** Sets a value for a different field (used for translate-all and cross-field streaming) */
  getOtherFieldValue?: (fieldName: string) => string;
  setOtherFieldValue?: (fieldName: string, value: string) => void;
  languageAiAssistantEnabled: boolean;
  subheadingsAiAssistantEnabled: boolean;
}

interface TranslationModalState {
  isShowing: boolean;
  language: string;
  onTranslateAll: () => void;
  onDismiss: () => void;
}

interface CustomizationFeedbackState {
  llmCustomizationUuid: string;
  hasFeedbackAccess: boolean;
  isShowingExtraContent: boolean;
}

interface UseWritingAssistantReturn {
  /** Diff state compatible with DSNoteInput.NoteField's diffState prop */
  diffState: DiffState | undefined;
  isLoading: boolean;
  isStreaming: boolean;
  /** Whether the loading spinner should show (loading but not yet streaming) */
  loadingBeforeStreaming: boolean;
  /** Whether loading or streaming is active */
  loadingBeforeAndDuringStreaming: boolean;
  /** Whether the user has access to the writing assistant feature */
  hasWritingAssistantAccess: boolean;
  /** CascadingDropdown options with callbacks injected */
  options: ReturnType<typeof getOptions>;
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
  /** Accept the AI-generated changes */
  acceptChanges: () => void;
  /** Reject the AI-generated changes */
  rejectChanges: () => void;
  /** Translation modal state (for subjective section translate-all prompt) */
  translationModal: TranslationModalState;
  /** Customization feedback state */
  customizationFeedback: CustomizationFeedbackState;
  /** Show the upgrade modal for users without writing assistant access */
  showUpgradeModal: boolean;
  setShowUpgradeModal: (value: boolean) => void;
  /** Raw afterContent for legacy display (before/after textareas) */
  afterContent: string | null;
  /** Whether the extra content area (diff + feedback) is showing */
  isShowingExtraContent: boolean;
}

export function useWritingAssistant({
  entityType,
  entityUuid,
  fieldName,
  getFieldValue,
  setFieldValue,
  getOtherFieldValue,
  setOtherFieldValue,
  languageAiAssistantEnabled,
  subheadingsAiAssistantEnabled,
}: UseWritingAssistantParams): UseWritingAssistantReturn {
  const {
    uuidField,
    sectionField,
    wsUpdateEvent,
    wsCompletionEvent,
  } = ENTITY_CONFIG[entityType];
  const isTemplate = entityType === "template";

  const [generateLlmCustomizations] = useMutation(
    GENERATE_LLM_CUSTOMIZATION,
  );
  const [acceptLlmCustomizationsMutation] = useMutation(
    ACCEPT_LLM_CUSTOMIZATIONS,
  );

  // State
  const [afterContent, setAfterContent] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [llmCustomizationUuid, setLlmCustomizationUuid] =
    useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [
    subjectiveTranslationLanguage,
    setSubjectiveTranslationLanguage,
  ] = useState<string | false>(false);
  const [isModalShowing, setIsModalShowing] = useState(false);
  const [isShowingExtraContent, setIsShowingExtraContent] =
    useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Hooks
  const { userUuid } = useAuth();
  const { connectStream, isStreaming } = useStreamResult();
  const { hasFeatureAccess } = usePremiumFeature();
  const { proposals: pendingProposals, resolveProposal } =
    usePendingWritingCompanionProposals();

  // Derived state
  const loadingBeforeStreaming = isLoading && !isStreaming;
  const loadingBeforeAndDuringStreaming = isLoading || isStreaming;
  const hasWritingAssistantAccess = hasFeatureAccess(
    UserPermissions.WRITING_ASSISTANT_ENABLED,
  );
  const hasFeedbackAccess = hasFeatureAccess(
    UserPermissions.NOTE_FEEDBACK_ENABLED,
  );

  // WebSocket handlers for companion LLM customization events
  const handleCustomizationUpdates = (
    message: Record<string, unknown>,
  ) => {
    const data = message.data as Record<string, unknown> | undefined;
    if (!data) return;
    if (
      data[uuidField] !== entityUuid ||
      String(data[sectionField]).toLowerCase() !==
        fieldName.toLowerCase()
    ) {
      return;
    }
    if (!isShowingExtraContent) {
      setIsShowingExtraContent(true);
    }
    if (!isLoading) {
      setIsLoading(true);
    }
    setAfterContent(data.after_content as string);
  };

  const handleCustomizationCompletion = (
    message: Record<string, unknown>,
  ) => {
    const data = message.data as Record<string, unknown> | undefined;
    if (!data) return;
    if (
      data[uuidField] !== entityUuid ||
      String(data[sectionField]).toLowerCase() !==
        fieldName.toLowerCase()
    ) {
      return;
    }
    const completedUuid = data.llm_customization_uuid as string;
    setLlmCustomizationUuid(completedUuid);
    setIsLoading(false);
    resolveSectionProposals(fieldName, completedUuid);
  };

  // The current proposal supersedes every other pending row for its section;
  // drop them so the next-oldest can't re-hydrate over a fresh proposal or
  // over content the user just accepted or rejected.
  const resolveSectionProposals = (
    section: string,
    currentUuid?: string,
  ) => {
    for (const proposal of pendingProposals) {
      if (
        proposal.uuid !== currentUuid &&
        proposal.noteUuid === entityUuid &&
        proposal.noteSection.toLowerCase() === section.toLowerCase()
      ) {
        resolveProposal(proposal.uuid);
      }
    }
  };

  useWatchUserWsEvent({
    onMessage: handleCustomizationCompletion,
    eventName: wsCompletionEvent,
  });

  // Hydrate a proposal the user never resolved: a Companion proposal recorded
  // while this section had no WS listener (refresh, other tab, late mount) or
  // a writing-assistant customization left mid-review. Equivalent to receiving
  // the updates + completion events; `llmCustomizationUuid` dedupes against a
  // completion event for the same row arriving before or after this.
  const pendingProposal = isTemplate
    ? undefined
    : selectPendingProposalForSection(
        pendingProposals,
        entityUuid,
        fieldName,
      );
  if (
    pendingProposal &&
    pendingProposal.uuid !== llmCustomizationUuid &&
    !isShowingExtraContent
  ) {
    setIsShowingExtraContent(true);
    setAfterContent(pendingProposal.afterContent ?? "");
    setLlmCustomizationUuid(pendingProposal.uuid);
    setIsLoading(false);
  }

  useWatchUserWsEvent({
    onMessage: handleCustomizationUpdates,
    eventName: wsUpdateEvent,
  });

  // Stream management
  const startStream = async (
    fieldNameArg: string,
    currentCustomizationLlmUuid: string,
  ) => {
    setAfterContent(null);
    if (fieldNameArg === fieldName) {
      setIsShowingExtraContent(true);
    } else if (setOtherFieldValue) {
      setOtherFieldValue(fieldNameArg, "");
    }

    const response = await connectStream(
      (message: string) => {
        if (fieldNameArg !== fieldName) {
          if (getOtherFieldValue && setOtherFieldValue) {
            setOtherFieldValue(
              fieldNameArg,
              getOtherFieldValue(fieldNameArg) + message,
            );
          }
        } else {
          setAfterContent((prev) => (prev || "") + message);
        }
      },
      () => {
        setIsLoading(false);
        if (fieldNameArg !== fieldName) {
          sendAcceptedStatusToServer(
            true,
            currentCustomizationLlmUuid,
            true,
            fieldNameArg,
          );
        }
      },
    );

    return (
      response ?? {
        taskUuid: "",
        error: true,
        streamComplete: Promise.resolve(),
      }
    );
  };

  // Send accept/reject status to server
  const sendAcceptedStatusToServer = async (
    accepted: boolean,
    llmCustomizationUuidArg?: string,
    translateAll = false,
    fieldNameArg = fieldName,
  ) => {
    const finalContent = accepted
      ? afterContent
      : fieldNameArg === fieldName
        ? getFieldValue()
        : getOtherFieldValue?.(fieldNameArg) || "";

    const finalLLMCustomizationUuid =
      llmCustomizationUuidArg || llmCustomizationUuid;
    const AcceptLLMCustomizationsInput = {
      llmCustomizationUuid: finalLLMCustomizationUuid,
      accepted,
      finalContent: finalContent ?? "",
    };
    setIsShowingExtraContent(false);
    resolveProposal(finalLLMCustomizationUuid);
    resolveSectionProposals(fieldNameArg, finalLLMCustomizationUuid);

    if (subjectiveTranslationLanguage && accepted && !translateAll) {
      setIsModalShowing(true);
    }

    const confirmedNoteCustomizationParams = {
      customizationUuid: finalLLMCustomizationUuid,
      entityType,
      entityUuid,
      accepted,
      noteSection: fieldNameArg,
    };
    mixpanel.track(
      "Confirmed Note Customization",
      confirmedNoteCustomizationParams,
    );

    try {
      await acceptLlmCustomizationsMutation({
        variables: {
          AcceptLLMCustomizationsInput,
        },
      });
    } catch (err) {
      const acceptingCustomizationErrorParams = {
        finalLLMCustomizationUuid,
        entityType,
        entityUuid,
        accepted,
        err,
      };
      mixpanel.track(
        "Accepting Customization Error",
        acceptingCustomizationErrorParams,
      );
    }
  };

  // Customization option click handler
  // Returns a promise that resolves when the stream finishes (for sequential translate-all)
  const onCustomizationOptionClick = async (
    parentValue: string,
    childValue: string,
    fieldNameArg = fieldName,
  ) => {
    if (!hasWritingAssistantAccess) {
      setShowUpgradeModal(true);
      return;
    }
    if (isTemplate) {
      alert(
        "warning",
        "Writing assistant is not supported for templates",
      );
      return;
    }

    const params = generateParams(
      parentValue,
      childValue,
      fieldNameArg,
    );
    const currentContent =
      fieldNameArg === fieldName
        ? getFieldValue()
        : getOtherFieldValue?.(fieldNameArg) || "";
    const currentCustomizationLlmUuid = uuidv4();

    if (!currentContent) {
      alert(
        "error",
        "There is no content to customize in this field!",
      );
      return;
    }

    try {
      setAfterContent(null);
      if (fieldNameArg === fieldName) {
        setIsLoading(true);
      }

      const streamResponse = await startStream(
        fieldNameArg,
        currentCustomizationLlmUuid,
      );

      if (streamResponse.taskUuid === "" || streamResponse.error) {
        alert(
          "error",
          "An error has occurred. Please try again later.",
        );
        setIsLoading(false);
        return;
      }

      const taskUuid = streamResponse.taskUuid;

      // Templates are not supported so this is always note
      const createLlmCustomizationInput = {
        noteUuid: entityUuid,
        noteSection: fieldNameArg,
        userUuid: userUuid ?? "",
        beforeContent: currentContent,
        actionType: parentValue,
        params: JSON.stringify(params),
        streamingTaskUuid: taskUuid,
        llmCustomizationUuid: currentCustomizationLlmUuid,
      };

      await generateLlmCustomizations({
        variables: {
          createLlmCustomizationInput,
        },
      });
      setLlmCustomizationUuid(currentCustomizationLlmUuid);
      resolveSectionProposals(
        fieldNameArg,
        currentCustomizationLlmUuid,
      );

      const customizedNoteParams = {
        customizationOption: parentValue,
        customizationSuboption: childValue,
        entityType,
        entityUuid,
        customizationUuid: currentCustomizationLlmUuid,
        noteSection: fieldNameArg,
        params,
      };

      mixpanel.track("Customized Note", customizedNoteParams);

      // Wait for the WebSocket stream to fully complete before returning
      await streamResponse.streamComplete;
    } catch (err) {
      const failedCustomizationParams = {
        customizationOption: parentValue,
        customizationSuboption: childValue,
        entityType,
        entityUuid,
        fieldNameArg,
        err,
      };

      mixpanel.track(
        "Customization Error",
        failedCustomizationParams,
      );
      setIsLoading(false);
    }
  };

  // Accept/reject handlers
  const acceptChanges = () => {
    if (afterContent !== null) {
      setFieldValue(afterContent);
    }
    setAfterContent(null);
    sendAcceptedStatusToServer(true);
  };

  const rejectChanges = () => {
    setAfterContent(null);
    sendAcceptedStatusToServer(false);
  };

  // Inject callbacks into cascading dropdown options
  const injectCallbacks = (
    options: ReturnType<typeof getOptions>,
  ) => {
    return options?.map((option) => ({
      ...option,
      items: option.items.map((item) => ({
        ...item,
        callback: () => {
          if (
            fieldName.toLowerCase() === "subjective" &&
            option.value === "translate_to"
          ) {
            setSubjectiveTranslationLanguage(item.value);
          } else {
            setSubjectiveTranslationLanguage("");
          }
          onCustomizationOptionClick(option.value, item.value);
        },
      })),
    }));
  };

  // Translate all sections handler - sequential to avoid concurrent stream errors
  const handleTranslateAll = async () => {
    setIsModalShowing(false);
    for (const mappedFieldName of noteSections) {
      const mappedContent =
        mappedFieldName === fieldName
          ? getFieldValue()
          : getOtherFieldValue?.(mappedFieldName);
      if (!mappedContent || mappedFieldName === fieldName) {
        continue;
      }
      await onCustomizationOptionClick(
        "translate_to",
        subjectiveTranslationLanguage as string,
        mappedFieldName,
      );
    }
  };

  // Build cascading dropdown options with injected callbacks
  const rawOptions = getOptions(
    fieldName,
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  );
  const options = injectCallbacks(rawOptions);

  // Build diff state for DSNoteInput integration
  const diffState: DiffState | undefined =
    isShowingExtraContent && afterContent !== undefined
      ? {
          before: getFieldValue(),
          after: afterContent,
          isLoading: loadingBeforeAndDuringStreaming,
        }
      : undefined;

  return {
    diffState,
    isLoading,
    isStreaming,
    loadingBeforeStreaming,
    loadingBeforeAndDuringStreaming,
    hasWritingAssistantAccess,
    options,
    isMenuOpen,
    setIsMenuOpen,
    acceptChanges,
    rejectChanges,
    translationModal: {
      isShowing: isModalShowing,
      language: subjectiveTranslationLanguage
        ? (subjectiveTranslationLanguage as string)
        : "",
      onTranslateAll: handleTranslateAll,
      onDismiss: () => setIsModalShowing(false),
    },
    customizationFeedback: {
      llmCustomizationUuid,
      hasFeedbackAccess,
      isShowingExtraContent,
    },
    showUpgradeModal,
    setShowUpgradeModal,
    afterContent,
    isShowingExtraContent,
  };
}
